import { useState, useMemo, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  InputAdornment,
  Box,
  Typography,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { useLoadOrganizationSpacesQuery } from "~/operations";

type SpaceOption = {
  id: string;
  mrn: string;
  name: string;
  description: string;
  hasAccess: boolean;
};

export type SpaceSearchAutocompleteProps = {
  orgMrn: string;
  existingSpaceMrns: string[];
  onSelectSpace: (space: { mrn: string; name: string }) => void;
  disabled?: boolean;
};

export function SpaceSearchAutocomplete({
  orgMrn,
  existingSpaceMrns,
  onSelectSpace,
  disabled = false,
}: SpaceSearchAutocompleteProps) {
  const [inputValue, setInputValue] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [open, setOpen] = useState(false);

  // Debounce the search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(inputValue);
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const { data, loading } = useLoadOrganizationSpacesQuery({
    variables: {
      mrn: orgMrn,
      query: debouncedSearchTerm || undefined,
      first: 50,
    },
    skip: !open && !debouncedSearchTerm,
  });

  const options = useMemo((): SpaceOption[] => {
    const spaces = data?.organization?.spacesList?.edges || [];
    return spaces
      .filter((edge) => edge?.node)
      .map((edge) => ({
        id: edge!.node!.id,
        mrn: edge!.node!.mrn,
        name: edge!.node!.name,
        description: edge!.node!.description || "",
        hasAccess: existingSpaceMrns.includes(edge!.node!.mrn),
      }));
  }, [data, existingSpaceMrns]);

  return (
    <Autocomplete
      fullWidth
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      loading={loading}
      disabled={disabled}
      value={null}
      inputValue={inputValue}
      onInputChange={(e, value) => setInputValue(value)}
      onChange={(e, value) => {
        if (value && !value.hasAccess) {
          onSelectSpace({ mrn: value.mrn, name: value.name });
          setInputValue("");
        }
      }}
      getOptionLabel={(option) => option.name}
      getOptionDisabled={(option) => option.hasAccess}
      isOptionEqualToValue={(option, value) => option.mrn === value.mrn}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder="Search to add a space"
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {params.InputProps.endAdornment}
                  <InputAdornment position="end">
                    <SearchIcon color="action" />
                  </InputAdornment>
                </>
              ),
            },
            htmlInput: {
              ...params.inputProps,
              "data-testid": "space-search-input",
            },
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box
            key={key}
            component="li"
            {...optionProps}
            data-testid={`space-search-option-${option.mrn}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              opacity: option.hasAccess ? 0.5 : 1,
              cursor: option.hasAccess ? "default" : "pointer",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2">{option.name}</Typography>
              {option.hasAccess && (
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontStyle: "italic" }}
                >
                  (Already has access)
                </Typography>
              )}
            </Box>
            {option.description && (
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                {option.description}
              </Typography>
            )}
          </Box>
        );
      }}
      noOptionsText={
        inputValue ? "No spaces found" : "Start typing to search for spaces..."
      }
    />
  );
}
